import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import {
  formatDay,
  parseIsoDay,
  REFERRAL_URGENCY_LABELS,
  SICK_SHEET_RECOMMENDATION_LABELS,
} from '@wodi/domain';
import type { ReferralLetterView, SickSheetView } from '@wodi/shared';
import { Button, Dialog, DialogActions, Tag } from '@wodi/ui';
import { useState } from 'react';
import { Link } from '@tanstack/react-router';

import { ApiError } from '../api/client.ts';
import { referralEnteredInError, sickSheetEnteredInError } from '../api/clinical-documents.ts';
import { formatInstant } from '../patients/format.ts';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import { toasts } from '../toasts.ts';
import styles from './documents.module.css';
import { ReferralPrinter, SickSheetPrinter } from './prints.tsx';

// Issued sick sheets and referral letters, newest first, each with its number (mono), what
// it says in brief, who issued it and when, and "Print" (a counted, audited copy). A
// mistake is corrected by "Entered in error" with a reason; the document stays in the list,
// marked, with the reason (CLAUDE.md: history is viewable).

type Issued =
  { kind: 'sick_sheet'; doc: SickSheetView } | { kind: 'referral'; doc: ReferralLetterView };

function dayWords(text: string): string {
  const day = parseIsoDay(text);
  return day.ok ? formatDay(day.value) : text;
}

function describe(item: Issued): { title: string; number: string; detail: string } {
  if (item.kind === 'sick_sheet') {
    const s = item.doc;
    return {
      title: 'Sick sheet',
      number: s.sickSheetNumber,
      detail: `${SICK_SHEET_RECOMMENDATION_LABELS[s.recommendation]}, ${dayWords(s.restFrom)} to ${dayWords(s.restTo)} (${s.days === 1 ? '1 day' : `${String(s.days)} days`})${s.diagnosisShown ? ', diagnosis shown' : ''}`,
    };
  }
  const r = item.doc;
  return {
    title: 'Referral letter',
    number: r.referralNumber,
    detail: `To ${r.toFacilityName}, ${REFERRAL_URGENCY_LABELS[r.urgency].toLowerCase()}`,
  };
}

export function IssuedDocumentList({
  sickSheets,
  referrals,
  listKeys,
  maySickSheets,
  mayReferrals,
  online,
}: {
  sickSheets: readonly SickSheetView[];
  referrals: readonly ReferralLetterView[];
  listKeys: readonly QueryKey[];
  maySickSheets: boolean;
  mayReferrals: boolean;
  online: boolean;
}) {
  const queryClient = useQueryClient();
  const [printing, setPrinting] = useState<Issued | null>(null);
  const [correcting, setCorrecting] = useState<Issued | null>(null);
  const items: Issued[] = [
    ...sickSheets.map((doc) => ({ kind: 'sick_sheet' as const, doc })),
    ...referrals.map((doc) => ({ kind: 'referral' as const, doc })),
  ].sort((a, b) => b.doc.issuedAt.localeCompare(a.doc.issuedAt));

  const correct = useMutation({
    mutationFn: async (reason: string): Promise<void> => {
      if (correcting === null) throw new Error('nothing chosen');
      if (correcting.kind === 'sick_sheet')
        await sickSheetEnteredInError(correcting.doc.id, reason);
      else await referralEnteredInError(correcting.doc.id, reason);
    },
    onSuccess: () => {
      toasts.show({ title: 'Marked entered in error' });
      setCorrecting(null);
      for (const key of listKeys) void queryClient.invalidateQueries({ queryKey: key });
    },
  });

  if (items.length === 0) {
    return <p className={styles.help}>No sick sheets or referral letters yet.</p>;
  }
  return (
    <>
      <ul className={styles.list} aria-label="Sick sheets and referral letters">
        {items.map((item) => {
          const words = describe(item);
          const may = item.kind === 'sick_sheet' ? maySickSheets : mayReferrals;
          const error = item.doc.enteredInError;
          return (
            <li key={item.doc.id} className={styles.item}>
              <div className={styles.itemMain}>
                <span className={styles.itemTitle}>
                  {`${words.title} `}
                  <span className={styles.mono}>{words.number}</span>
                </span>
                <span className={styles.meta}>{words.detail}</span>
                <span className={styles.meta}>
                  {`Issued ${formatInstant(item.doc.issuedAt)} by ${item.kind === 'sick_sheet' ? item.doc.issuedBy.displayName : item.doc.author.displayName}${item.doc.printCount === 0 ? ', not printed yet' : `, printed ${String(item.doc.printCount)} ${item.doc.printCount === 1 ? 'time' : 'times'}`}`}
                </span>
                {error === null ? null : (
                  <span className={styles.meta}>
                    {`Entered in error ${formatInstant(error.at)} by ${error.by.displayName}: ${error.reason}`}
                  </span>
                )}
                {item.kind === 'referral' && item.doc.referralId !== null ? (
                  <Link
                    className={styles.meta}
                    to="/referrals/$referralId"
                    params={{ referralId: item.doc.referralId }}
                  >
                    Follow the referral
                  </Link>
                ) : null}
              </div>
              {error === null ? null : <Tag state="neutral">Entered in error</Tag>}
              <div className={styles.itemActions}>
                <Button
                  variant="secondary"
                  isDisabled={!online}
                  onPress={() => {
                    setPrinting(item);
                  }}
                >
                  Print
                </Button>
                {may && error === null ? (
                  <Button
                    variant="quiet"
                    isDisabled={!online}
                    onPress={() => {
                      setCorrecting(item);
                    }}
                  >
                    Entered in error
                  </Button>
                ) : null}
              </div>
            </li>
          );
        })}
      </ul>
      {printing === null ? null : (
        <Dialog
          title={printing.kind === 'sick_sheet' ? 'Print sick sheet' : 'Print referral letter'}
          isOpen
          onOpenChange={(open) => {
            if (!open) setPrinting(null);
          }}
        >
          {printing.kind === 'sick_sheet' ? (
            <SickSheetPrinter id={printing.doc.id} listKeys={listKeys} />
          ) : (
            <ReferralPrinter id={printing.doc.id} listKeys={listKeys} />
          )}
          <DialogActions>
            <Button
              onPress={() => {
                setPrinting(null);
              }}
            >
              Done
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {correcting === null ? null : (
        <ReasonDialog
          title={
            correcting.kind === 'sick_sheet'
              ? 'Mark sick sheet entered in error'
              : 'Mark referral letter entered in error'
          }
          isOpen
          onOpenChange={(open) => {
            if (!open) setCorrecting(null);
          }}
          confirmLabel="Mark entered in error"
          pendingLabel="Saving…"
          isPending={correct.isPending}
          error={correct.error instanceof ApiError ? correct.error.message : null}
          onConfirm={(reason) => {
            correct.mutate(reason);
          }}
        >
          <p className={styles.help}>
            It stays in the history, marked. Issue a new one if the patient needs it.
          </p>
        </ReasonDialog>
      )}
    </>
  );
}
